import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/auth.context';
import { phoneNumber, signIn } from '@/lib/auth-client';
import { clearOtpCache } from '@/services/otp';

interface UseOTPVerificationOptions {
  identifier: string;
  isEmail: boolean;
  onResend: () => void;
  /**
   * Optional callback called after successful OTP verification.
   * If not provided, defaults to redirecting to /dashboard
   */
  onSuccess?: () => void | Promise<void>;
}

export function useOTPVerification(options: UseOTPVerificationOptions) {
  const { identifier, isEmail, onResend, onSuccess } = options;
  const { t } = useTranslation();

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const { refetch: refetchSession } = useAuth();

  const handleSubmit = async (code: string) => {
    const sanitized = code.replace(/\D/g, '');
    if (sanitized.length !== 6) {
      setError(t('formErrors.otpLength'));
      return;
    }

    const isDev = import.meta.env.VITE_DEV_BYPASS_OTP === 'true';
    const devOtpCode = import.meta.env.VITE_DEV_USER_OTP_CODE ?? '000000';
    const isBypassCode = sanitized === devOtpCode;

    if (!(isDev && isBypassCode)) {
      setIsSubmitting(true);
      setError('');
    }

    try {
      if (isDev && isBypassCode && !isEmail) {
        if (import.meta.env.DEV) {
          console.log('[DEV_BYPASS_OTP] Using normal verification with dev code');
        }
        const { error: verifyError } = await phoneNumber.verify({
          phoneNumber: identifier,
          code: sanitized,
        });

        if (verifyError) {
          console.error('[DEV_BYPASS_OTP] Verification failed', verifyError);
          throw new Error(
            t(`errors.${verifyError.code}`) !== `errors.${verifyError.code}`
              ? t(`errors.${verifyError.code}`)
              : t('formErrors.invalidOTP'),
          );
        }

        if (import.meta.env.DEV) {
          console.log('[DEV_BYPASS_OTP] Verification successful');
        }

        await clearOtpCache(identifier, false);

        await refetchSession();

        setTimeout(() => {
          window.location.replace('/');
        }, 500);
        return;
      } else if (isEmail) {
        const { error: signInError } = await signIn.emailOtp({
          email: identifier,
          otp: sanitized,
        });

        if (signInError) {
          const errorCode = signInError.code || 'INVALID_OTP';
          const errorMessage =
            t(`errors.${errorCode}`) !== `errors.${errorCode}`
              ? t(`errors.${errorCode}`)
              : t('formErrors.invalidOTP');
          throw new Error(errorMessage);
        }
      } else {
        const { data: verifyData, error: verifyError } = await phoneNumber.verify({
          phoneNumber: identifier,
          code: sanitized,
        });

        if (verifyError) {
          console.error('[OTP verify] error', verifyError);
          throw new Error(
            t(`errors.${verifyError.code}`) !== `errors.${verifyError.code}`
              ? t(`errors.${verifyError.code}`)
              : t('formErrors.invalidOTP'),
          );
        }

        if (import.meta.env.DEV) {
          console.log('[OTP verify] success', { data: verifyData });
        }
      }

      await clearOtpCache(identifier, isEmail);

      await refetchSession();

      if (onSuccess) {
        await onSuccess();
      } else {
        if (import.meta.env.DEV) {
          console.log('[OTP verify] reloading to /');
        }
        setTimeout(() => {
          window.location.replace('/');
        }, 300);
      }
    } catch (err) {
      console.error('OTP verification error', err);
      setError(t('formErrors.invalidOTP'));
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (value: string) => {
    setCode(value);
    if (error) setError('');
  };

  const handleResend = async () => {
    try {
      await onResend();

      setCode('');

      // Show success toast
      const method = isEmail ? t('dashboard.auth.otp.byEmail') : t('dashboard.auth.otp.bySMS');
      toast.success(t('dashboard.auth.otp.resendSuccess', { method, identifier }), {
        duration: 4000,
      });
    } catch {
      setError(t('formErrors.resendFailed'));
    }
  };

  const handleManualSubmit = () => {
    handleSubmit(code);
  };

  return {
    code,
    handleChange,
    error,
    isSubmitting,
    handleResend,
    handleManualSubmit,
  };
}
